import isEqual from 'lodash/isEqual';
import * as React from 'react';
import {
  ITestsSetting,
  testsSettingProvider,
} from '../../data/providers/settings/testsSettingProvider';
import {
  IInifiniteTestQuery,
  infiniteTests,
} from '../../data/providers/testsProvider';
import { getTestTypeLabel } from '../../data/providers/testTypesProvider';
import { EntityType, IAthlete, IRange, ITag, ITest } from '../../data/types';
import { ISelectedTagMap } from '../../ui/sections/tests/edit/EditTests';
import { queryHashGenerator } from '../../utils/queryHash';
import { useEffectWhen } from '../useEffectWhen';

interface IUseInfiniteTests {
  testsLoading: boolean;
  tests: ITest[];
  setStartAfter: (id: number) => void;
  onEntityChange: (entity: IInifiniteTestsArgs) => void;
  dateRange?: IRange;
  facet?: string;
  clearDateRange?: () => void;
  updateInPlace: (updateToApply: IUpdateInPlace) => void;
  showDisabledTests: boolean;
}

interface IEntityDefinition {
  entityId: string;
  entityType: EntityType;
  range?: IRange;
}

export interface IUpdateInPlace {
  action: 'delete' | 'change-tags' | 'change-athlete' | 'enable';
  selectedTests: Record<string, boolean>;
  entities?: IAthlete | ISelectedTagMap;
}

export interface IInifiniteTestsArgs extends IEntityDefinition {
  showDisabledTests?: boolean;
}

/**
 * @description returns an object including tests and if the initial load is happening,
 * or if the tests is just an empty array
 * @param args
 */
export const useInfiniteTests = (
  props: IInifiniteTestsArgs
): IUseInfiniteTests => {
  const [tests, setTests] = React.useState<ITest[]>([]);
  const [testsLoading, setTestsLoading] = React.useState(true);
  const [queryParams, setQueryParams] = React.useState<IInifiniteTestQuery>({
    ...props,
  });

  React.useEffect((): (() => void) => {
    const subscriber = testsSettingProvider().subscribe(
      (setting: ITestsSetting): void => {
        /// if server state matches component state, ignore update
        if (
          isEqual(queryParams?.range, setting?.range) &&
          isEqual(queryParams?.facet, setting?.facet)
        ) {
          return;
        }

        setQueryParams({
          ...queryParams,
          startAfter: null,
          range: setting?.range,
          facet: setting?.facet,
        });
      }
    );
    return (): void => subscriber.unsubscribe();
  }, [JSON.stringify(queryParams)]);

  /**
   * @description handles entity change
   * @param mEntity
   */
  const onEntityChange = (mEntity: IInifiniteTestQuery): void => {
    setQueryParams({
      entityId: mEntity.entityId,
      entityType: mEntity.entityType,
      startAfter: undefined,
      range: queryParams?.range,
      facet: queryParams?.facet,
      showDisabledTests: mEntity.showDisabledTests,
    });
  };
  /**
   * @description applies an update to the current list of tests
   * @param tests
   */
  const updateInPlace = (updateToApply: IUpdateInPlace): void => {
    const updatedTests: ITest[] = [];
    switch (updateToApply.action) {
      /// handle the changing of tags
      case 'change-tags':
        for (const test of tests) {
          /// check if the test is actually selected
          if (updateToApply.selectedTests.hasOwnProperty(test.id)) {
            const updatedTest = { ...test };
            const updatedIds: string[] = [];
            const updatedTags: ITag[] = [];
            for (const tagId in updateToApply.entities as ISelectedTagMap) {
              if (typeof tagId === 'string') {
                updatedIds.push(tagId);
                updatedTags.push(updateToApply.entities[tagId].tag);
              }
            }
            /// generate the new query hash for the test
            const updatedQueryHash = queryHashGenerator(
              test.testType.id,
              updatedIds
            );
            updatedTest.tags = updatedTags;
            updatedTest.queryHash = updatedQueryHash;
            updatedTests.push(updatedTest);
          } else {
            updatedTests.push(test);
          }
        }
        break;

      case 'delete':
        for (const test of tests) {
          if (!updateToApply.selectedTests.hasOwnProperty(test.id)) {
            updatedTests.push(test);
          }
        }
        break;

      case 'enable':
        for (const test of tests) {
          if (!updateToApply.selectedTests.hasOwnProperty(test.id)) {
            updatedTests.push(test);
          }
        }
        break;

      case 'change-athlete':
        for (const test of tests) {
          if (updateToApply.selectedTests.hasOwnProperty(test.id)) {
            const updatedTest = { ...test };
            updatedTest.athlete = updateToApply.entities as IAthlete;
            updatedTest.athleteId = (updateToApply.entities as IAthlete).id;
            updatedTests.push(updatedTest);
          } else {
            updatedTests.push(test);
          }
        }
    }
    setTests(updatedTests);
  };

  /**
   * @description sets start after param (used when fetching next batch of tests)
   * @param value
   */
  const setStartAfter = (value: number): void => {
    setQueryParams({
      ...queryParams,
      startAfter: value,
    });
  };

  /**
   * @description runs only when startAfter changes and appends the result to the already fetched data
   */
  useEffectWhen(
    (): void => {
      /// only run if startAfter is defined
      if (queryParams?.startAfter !== undefined) {
        setTestsLoading(true);
        infiniteTests(queryParams).then((data: ITest[]): void => {
          setTests((mTests: ITest[]): ITest[] => mTests.concat(data));
          setTestsLoading(false);
        });
      }
    },
    [queryParams?.startAfter, queryParams?.entityId, queryParams?.entityType],
    [queryParams?.startAfter]
  );

  /**
   * @description runs when the query parameters change and overwrites any already fetched data
   */
  useEffectWhen(
    (): void => {
      if (queryParams?.entityId && queryParams?.entityType) {
        setTestsLoading(true);
        setTests([]);
        infiniteTests(queryParams).then((data: ITest[]): void => {
          setTests(data);
          setTestsLoading(false);
        });
      }
    },
    [
      queryParams?.startAfter,
      queryParams?.entityId,
      queryParams?.entityType,
      queryParams?.range,
      queryParams?.facet,
    ],
    [
      queryParams?.entityId,
      queryParams?.entityType,
      queryParams?.showDisabledTests,
    ]
  );

  /**
   * @description runs when the date range changes and overwrites any already fetched data
   */
  useEffectWhen(
    (): void => {
      if (queryParams?.entityId && queryParams?.entityType) {
        setTestsLoading(true);
        setTests([]);
        infiniteTests(queryParams).then((data: ITest[]): void => {
          setTests(data);
          setTestsLoading(false);
        });
      }
    },
    [
      queryParams?.startAfter,
      queryParams?.entityId,
      queryParams?.entityType,
      queryParams?.range,
    ],
    [JSON.stringify(queryParams?.range)]
  );

  /**
   * @description runs when the facet changes and overwrites any already fetched data
   */
  useEffectWhen(
    (): void => {
      if (queryParams?.entityId && queryParams?.entityType) {
        setTestsLoading(true);
        setTests([]);
        infiniteTests(queryParams).then((data: ITest[]): void => {
          setTests(data);
          setTestsLoading(false);
        });
      }
    },
    [
      queryParams?.startAfter,
      queryParams?.entityId,
      queryParams?.entityType,
      queryParams?.range,
      queryParams?.facet,
    ],
    [JSON.stringify(queryParams?.facet)]
  );

  return {
    updateInPlace,
    testsLoading,
    tests,
    setStartAfter,
    onEntityChange,
    dateRange: queryParams?.range,
    facet: queryParams?.facet ? getTestTypeLabel(queryParams.facet) : null,
    showDisabledTests: queryParams?.showDisabledTests ?? false,
  };
};
